/**
 * The create-pool "How do you want to set this up?" step (ISSUE-202, R1 to R5).
 *
 * Platform spec: .agents/docs/design/platforms/web.md. This step is presented
 * inside the existing create-pool Dialog, so it inherits that component's
 * focus trap, its dismiss behavior and its responsive sheet/modal flip
 * rather than restating any of it. Choices are radios, not buttons, so the
 * group is one tab stop and arrow keys move within it.
 *
 * The iOS and Android specs are deliberately not named: the apps/mobile half
 * of ISSUE-202 is out of scope for this card.
 */
import { InlineBanner, Radio, Skeleton } from "@sportsmet/ui";
import type { ReactNode } from "react";

import type { typeQuickStartOption } from "./createPoolDraft";

export type typeCreatePoolSetupChoice = "quickStart" | "myself";

export type typeCreatePoolSetupStepProps = {
  choice: typeCreatePoolSetupChoice | null;
  quickStarts: readonly typeQuickStartOption[];
  selectedQuickStartId: string | null;
  isLoading: boolean;
  hasFailed: boolean;
  onRetry: () => void;
  onChooseChoice: (choice: typeCreatePoolSetupChoice) => void;
  onChooseQuickStart: (option: typeQuickStartOption) => void;
};

export const CREATE_POOL_QUICK_START_LABEL = "Quick start";
export const CREATE_POOL_QUICK_START_DESCRIPTION =
  "Start from a ready-made setup.";
export const CREATE_POOL_MYSELF_LABEL = "Set it up myself";
export const CREATE_POOL_MYSELF_DESCRIPTION = "Choose every rule yourself.";
export const CREATE_POOL_QUICK_START_NOTE = "You can change any of this later.";
export const CREATE_POOL_QUICK_START_FAILED =
  "We could not load the Quick starts.";

/** Step 2: Quick start or set it up myself. */
export const CreatePoolSetupStep = (
  props: typeCreatePoolSetupStepProps,
): ReactNode => {
  if (props.isLoading) {
    return (
      <div className="flex flex-col gap-4" data-testid="create-pool-step-setup">
        <Skeleton shape="control" label="Loading the Quick starts" />
        <Skeleton shape="control" />
      </div>
    );
  }

  const hasQuickStarts = !props.hasFailed && props.quickStarts.length > 0;

  return (
    <div className="flex flex-col gap-4" data-testid="create-pool-step-setup">
      <Radio
        label="How do you want to set this up?"
        labelHidden
        items={
          hasQuickStarts
            ? [
                {
                  value: "quickStart",
                  label: CREATE_POOL_QUICK_START_LABEL,
                  description: CREATE_POOL_QUICK_START_DESCRIPTION,
                },
                {
                  value: "myself",
                  label: CREATE_POOL_MYSELF_LABEL,
                  description: CREATE_POOL_MYSELF_DESCRIPTION,
                },
              ]
            : [
                {
                  value: "myself",
                  label: CREATE_POOL_MYSELF_LABEL,
                  description: CREATE_POOL_MYSELF_DESCRIPTION,
                },
              ]
        }
        value={hasQuickStarts ? (props.choice ?? undefined) : "myself"}
        onValueChange={(next) => {
          props.onChooseChoice(next as typeCreatePoolSetupChoice);
        }}
      />
      {props.hasFailed ? (
        <div data-testid="create-pool-quick-starts-failed">
          <InlineBanner
            variant="warning"
            message={CREATE_POOL_QUICK_START_FAILED}
            action={{ label: "Try again", onAction: props.onRetry }}
          />
        </div>
      ) : null}
      {hasQuickStarts && props.choice === "quickStart" ? (
        <div data-testid="create-pool-quick-start-list">
          <Radio
            label="Pick a setup"
            items={props.quickStarts.map((option) => ({
              value: option.quickStartId,
              label: option.title,
              description: option.summary,
            }))}
            value={props.selectedQuickStartId ?? undefined}
            onValueChange={(next) => {
              const match = props.quickStarts.find(
                (option) => option.quickStartId === next,
              );
              if (match !== undefined) props.onChooseQuickStart(match);
            }}
          />
          <p className="text-caption text-ink-secondary">
            {CREATE_POOL_QUICK_START_NOTE}
          </p>
        </div>
      ) : null}
    </div>
  );
};
